import Menu from '@mui/material/Menu'
import React, { memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon, Typography } from '@mui/material'
import { colors } from 'src/theme'
import { ActionButton } from 'src/components/shared/ActionButton'

interface TableSelectOption<V> {
  value: V
  component: React.ReactNode
}

interface Props<V extends string> {
  title?: string
  icon?: any
  options: TableSelectOption<V>[]
  selectedValues: Array<V>
  emptyText?: string
  onEmpty?(): void
  onSelect(values: Array<V>): void
}

const TableSelectComponent = <V extends string>({
  title = 'Select',
  icon,
  options,
  selectedValues,
  emptyText,
  onSelect,
  onEmpty,
}: Props<V>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleToggle = useCallback(
    (value: V) => {
      const currentIndex = selectedValues.indexOf(value)

      if (currentIndex === -1) {
        onSelect([...selectedValues, value])
      } else {
        onSelect([
          ...selectedValues.slice(0, currentIndex),
          ...selectedValues.slice(currentIndex + 1),
        ])
      }
    },
    [selectedValues],
  )

  const handleClear = useCallback(() => {
    onSelect([])
  }, [])

  const getItemChecked = useCallback(
    (value: V) => {
      return selectedValues.includes(value)
    },
    [selectedValues],
  )

  const handleEmpty = useCallback(() => {
    handleClose()

    if (onEmpty) {
      onEmpty()
    }
  }, [onEmpty])

  return (
    <>
      <ActionButton
        className={tw`px-4`}
        selected={!!anchorEl}
        color="white"
        text={title}
        icon={
          selectedValues?.length ? (
            <div css={tw`w-5 h-5 items-center justify-center flex rounded bg-[#7F00FF]`}>
              <Typography variant="overline" color="white">
                {selectedValues.length}
              </Typography>
            </div>
          ) : (
            icon
          )
        }
        onClick={handleClick}
      />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        css={tw`mt-2 rounded-xl`}
      >
        {options.length ? (
          <div>
            <div css={tw`px-4 py-2 flex items-center justify-between`}>
              <Typography variant="h5">Select:</Typography>
              {selectedValues?.length ? (
                <div css={tw`cursor-pointer hover:opacity-50`} onClick={handleClear}>
                  <Typography variant="subtitle1" color={colors.PRIMARY_PURPLE_500}>
                    Clear all
                  </Typography>
                </div>
              ) : null}
            </div>
            <List>
              {options.map(({ value, component }) => {
                const labelId = `checkbox-list-label-${name}`

                return (
                  <ListItem css={tw`w-[296px]`} key={value} disablePadding>
                    <ListItemButton role={undefined} onClick={() => handleToggle(value)} dense>
                      <ListItemIcon css={tw`min-w-0 mr-2`}>
                        <Checkbox
                          edge="start"
                          checked={getItemChecked(value)}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </ListItemIcon>
                      {typeof component === 'string' ? (
                        <Typography variant="subtitle1">{component}</Typography>
                      ) : (
                        component
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </div>
        ) : (
          <div css={tw`px-6 py-4 w-[240px]`}>
            <Typography css={tw`text-center mb-0.5`} variant="h4">
              No options were created
            </Typography>
            <Typography css={tw`text-center`} variant="body2" color={colors.SHADES_GREY_400}>
              {emptyText}
            </Typography>
            {onEmpty && (
              <Typography
                css={tw`text-center cursor-pointer mt-2`}
                color={colors.PRIMARY_PURPLE_500}
                variant="subtitle1"
                onClick={handleEmpty}
              >
                Manage options
              </Typography>
            )}
          </div>
        )}
      </Menu>
    </>
  )
}

export const TableSelect = memo(TableSelectComponent) as typeof TableSelectComponent
